import { log } from '@/utils/log';
import { defineStore } from 'pinia';

export type ToastType = 'success' | 'error' | 'info' | 'warning';
export interface Toast {
    id: number;
    type: ToastType;
    message: string;
    timeout?: number;
}

let _id = 0;

// Track recent toasts to prevent duplicates
const recentToasts = new Map<string, number>();
const DUPLICATE_THRESHOLD = 1000; // 1 second

export const useToastStore = defineStore('toast', {
    state: () => ({
        items: [] as Toast[],
        // Error toasts are dropped until this moment (epoch ms), see holdBackErrors().
        errorsHeldUntil: 0,
    }),
    actions: {
        push(payload: Omit<Toast, 'id'>) {
            if (payload.type === 'error' && Date.now() < this.errorsHeldUntil) {
                log('Error toast held back, a notice already explains the failure:', payload.message);
                return _id;
            }

            // Create a unique key for this toast based on type and message
            const toastKey = `${payload.type}:${payload.message}`;
            const now = Date.now();

            // Check if this exact toast was shown recently
            const lastShown = recentToasts.get(toastKey);
            if (lastShown && now - lastShown < DUPLICATE_THRESHOLD) {
                // Don't show duplicate toast, just return the last id
                return _id;
            }

            // Track this toast
            recentToasts.set(toastKey, now);

            // Clean up old entries from recentToasts
            for (const [key, timestamp] of recentToasts.entries()) {
                if (now - timestamp > DUPLICATE_THRESHOLD) {
                    recentToasts.delete(key);
                }
            }

            const id = ++_id;
            const toast: Toast = { id, timeout: 3500, ...payload };
            this.items.push(toast);
            if (toast.timeout && toast.timeout > 0) {
                setTimeout(() => this.remove(id), toast.timeout);
            }
            return id;
        },
        remove(id: number) {
            this.items = this.items.filter((t) => t.id !== id);
        },
        clear() {
            this.items = [];
        },
        /**
         * Drop error toasts for the next `ms` milliseconds. For a refusal that
         * has a notice of its own (a 429, see services/api.ts): the caller's
         * catch block reports the same failed request right after, and two
         * toasts for one cause read as two problems.
         */
        holdBackErrors(ms: number) {
            this.errorsHeldUntil = Math.max(this.errorsHeldUntil, Date.now() + ms);
        },
        success(message: string, timeout = 5000) {
            this.push({ type: 'success', message, timeout });
        },
        error(message: string, timeout = 5000) {
            this.push({ type: 'error', message, timeout });
        },
        info(message: string, timeout = 3500) {
            this.push({ type: 'info', message, timeout });
        },
        warning(message: string, timeout = 4000) {
            this.push({ type: 'warning', message, timeout });
        },
    },
});
